import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRef, useMemo, useEffect } from 'react';
import { styled, css } from 'styled-components';
import { useQueryUserInfo } from '../../../user/queries/useQueryUserInfo.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssTextEllipsis } from '@fabric-app/ui/constants/styled-utils';
import { ChatbotConversationUserActionType, ChatbotConversationUserActionSubType } from '../../types.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import AvatarUser from '../../../user/components/AvatarUser/AvatarUser.js';
import Avatar from '../../../user/components/Avatar/Avatar.js';
import { isChatbotUserMessage, isChatbotAssistantMessage, isChatbotContextSwitchInfo } from '../../utils/guardsChatbotMessage.js';
import { parseMarkdown } from '@fabric-app/ui/markdown/markdown.utils';
import { ChatbotMessageActionsMinChars } from '../../constants.js';
import { ChatbotMessageActions } from './ChatbotMessageActions.js';
import { useShowExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { ChatbotMessageSummaryBox } from './ChatbotMessageSummaryBox.js';
import { motion } from 'framer-motion';
import { framerAnimationSlideUp } from '@fabric-app/ui/constants/framerAnimations';
import { isNumber } from '@fabric-app/utils/guards';
import { randomInt } from '@fabric-app/utils/random';
import { createRoot } from '../../../../node_modules/react-dom/client.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { IconOther12CheckboxTick, IconDesktop16Cross, IconPath16FabricFace } from '@fabric-app/icons/svg';
import { flushSync } from 'react-dom';
import { isInServerPlatform } from '@fabric-app/compat/capabilities/platform';
import { NO_PERMISSION_DRAFT_MARKER, INCOMPLETE_DRAFT_MARKER, COMPLETE_DRAFT_MARKER, PENDING_DRAFT_MARKER } from '../../utils/filterDiffTags.js';
import { useChatbot } from '../../hooks/useChatbot.js';
import { useIsFeatureFlagEnabled, FeatureFlag } from '../../../feature-flags/feature-flags.store.js';
import { ChatbotMessageReferencedItems } from './ChatbotMessageReferencedItems.js';
import { useAssistantStore } from '../../stores/assistantStore.js';
import { omitProperties } from '@fabric-app/utils/object/manipulation';

function isValidUUID(uuid) {
  const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
  return regex.test(uuid);
}
const RenderedContentWrapper = styled.div.withConfig({
  displayName: "ChatbotMessage__RenderedContentWrapper",
  componentId: "sc-cc263401-0"
})([
  `-webkit-user-select:auto;*{user-select:auto;-webkit-user-select:auto;}font-size:0.875rem;line-height:1.375rem;p,ul,ol{font-size:0.875rem;line-height:1.375rem;}`,
  ` h1,h2,h3,h4,h5,h6{font-weight:700;}overflow-wrap:break-word;word-break:break-word;hyphens:auto;p:not(:first-child){margin-top:0.5em;}p:not(:last-child){margin-bottom:0.5em;}a{display:inline;}line-height:18px;word-wrap:break-word;hyphens:auto;white-space:pre-wrap;p:not([class]){white-space:pre-wrap;}> *:not(p){white-space:normal;}user-select:text;*{user-select:text;}a:not([class]){color:rgb(var(--fabric-color-primary));text-decoration:underline;}a.resource-reference{display:inline-block;padding:0 5px;border-radius:6px;background:rgba(255,255,255,0.3);max-width:240px;`,
  `;vertical-align:bottom;&:hover{background:rgba(255,255,255,0.2);}}code:not([class]){font-family:'Fira Code',monospace;font-size:13px;background:var(--fabric-color-bg-tertiary);padding:2px 4px;border-radius:4px;color:var(--fabric-color-text-tertiary);}pre:not([class]){font-family:'Fira Code',monospace;font-size:13px;background:var(--fabric-color-bg-tertiary);padding:8px;border-radius:4px;color:var(--fabric-color-text-tertiary);overflow-x:auto;white-space:pre-wrap;> code{background:none;padding:0;border-radius:0;}}ul:not([class]){list-style-type:disc;padding-left:1.5rem;margin-top:0.5rem;margin-bottom:0.5rem;> li{margin-top:0.5rem;margin-bottom:0.5rem;}}ol:not([class]){list-style-type:decimal;padding-left:1.5rem;margin-top:0.5rem;margin-bottom:0.5rem;> li{margin-top:0.5rem;margin-bottom:0.5rem;}}`
], (p) => p.isResourceList && css([
  `ol,ul{margin-bottom:0;margin-top:0.5rem;li{margin-top:0;margin-bottom:1rem;&:after{content:'';display:block;clear:both;}div[data-prepend-portal='true']{float:left;margin-right:1rem;[data-preview-resource-external-link]{display:none;}}}& > *{margin-top:1rem;}}`
]), cssTextEllipsis);
const referenceMdLinkWithLocationReferenceRegex = /\[([^\]]*)\]\(fabric:\/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\?(time|page)=(\d*\.?\d*))?\)/g;
const replaceFn = (match, label, resourceId, type, locationValue) => {
  const params = new URLSearchParams(omitProperties({
    class: "resource-reference",
    "data-type": type ? "location_reference" : void 0,
    "data-resource-id": resourceId,
    "data-ref-type": type,
    "data-ref-value": locationValue
  }, [
    omitProperties.isNullOrUndefined
  ]));
  return `${match}<!--rehype:${params.toString()}-->`;
};
const replaceItemLocationReferenceWithMarkdownLink = (content) => {
  const f = content.matchAll(referenceMdLinkWithLocationReferenceRegex);
  const referenceItems = [];
  for (const match of f) {
    referenceItems.push({
      resourceId: match[2],
      type: match[3],
      value: match[4],
      label: match[1]
    });
  }
  return {
    content: content.replaceAll(referenceMdLinkWithLocationReferenceRegex, replaceFn),
    referenceItems
  };
};
const DraftMarkerSpan = styled.span.withConfig({
  displayName: "ChatbotMessage__DraftMarkerSpan",
  componentId: "sc-cc263401-1"
})([
  `display:inline-flex;align-items:center;gap:0.5rem;color:`,
  `;`
], cssVar["color-text-secondary"]);
const DraftMarker = ({ marker }) => {
  return /* @__PURE__ */ jsxs(DraftMarkerSpan, {
    "data-draft-marker": marker,
    children: [
      marker === PENDING_DRAFT_MARKER && /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(Spinner, {
            as: "span",
            color: cssVar["color-text-secondary"],
            size: 16
          }),
          "Applying draft changes to note."
        ]
      }),
      (marker === COMPLETE_DRAFT_MARKER || marker === INCOMPLETE_DRAFT_MARKER) && /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(IconOther12CheckboxTick, {}),
          "Draft changes applied to note."
        ]
      }),
      marker === NO_PERMISSION_DRAFT_MARKER && /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsx(IconDesktop16Cross, {}),
          "Unable to apply draft changes to note."
        ]
      })
    ]
  });
};
const setupMarker = (marker) => {
  if (isInServerPlatform()) {
    return "";
  }
  const div = document.createElement("div");
  const root = createRoot(div);
  flushSync(() => {
    root.render(/* @__PURE__ */ jsx(DraftMarker, {
      marker
    }));
  });
  const content = div.innerHTML;
  root.unmount();
  return content;
};
const markerToHTML = {
  [PENDING_DRAFT_MARKER]: setupMarker(PENDING_DRAFT_MARKER),
  [COMPLETE_DRAFT_MARKER]: setupMarker(COMPLETE_DRAFT_MARKER),
  [INCOMPLETE_DRAFT_MARKER]: setupMarker(INCOMPLETE_DRAFT_MARKER),
  [NO_PERMISSION_DRAFT_MARKER]: setupMarker(NO_PERMISSION_DRAFT_MARKER)
};
const RenderedContent = ({ content = "", isResourceList }) => {
  const showExpandedResource = useShowExpandedResource();
  const setChatAssistantFullscreen = useAssistantStore((s) => s.setChatAssistantFullscreen);
  const divRef = useRef(null);
  const { markdownContent, referenceItems } = useMemo(() => {
    const { content: parsedReference, referenceItems: referenceItems2 } = replaceItemLocationReferenceWithMarkdownLink(content || "");
    const parsedMessage = parseMarkdown(parsedReference, {
      autoLinks: true,
      includeEmptyLines: false
    });
    return {
      markdownContent: parsedMessage.replaceAll(PENDING_DRAFT_MARKER, markerToHTML[PENDING_DRAFT_MARKER]).replaceAll(COMPLETE_DRAFT_MARKER, markerToHTML[COMPLETE_DRAFT_MARKER]).replaceAll(INCOMPLETE_DRAFT_MARKER, markerToHTML[INCOMPLETE_DRAFT_MARKER]).replaceAll(NO_PERMISSION_DRAFT_MARKER, markerToHTML[NO_PERMISSION_DRAFT_MARKER]),
      referenceItems: referenceItems2
    };
  }, [
    content
  ]);
  useEffect(() => {
    const div = divRef.current;
    const linkClickHandler = (e) => {
      const target = e.target;
      if (target.getAttribute("data-type") === "location_reference") {
        const resourceId = target.getAttribute("data-resource-id");
        const refType = target.getAttribute("data-ref-type");
        const refValue = target.getAttribute("data-ref-value");
        const value = isNumber(Number(refValue)) ? Number(refValue) : void 0;
        if (!resourceId || !refType || typeof value !== "number") {
          e.preventDefault();
          console.error("Invalid location reference", resourceId, refType, refValue);
          return;
        }
        const valueWithOffset = value + randomInt(100, 999) / 1e6;
        const queryParams = refType === "time" ? {
          t: valueWithOffset,
          p: void 0
        } : {
          p: valueWithOffset,
          t: void 0
        };
        showExpandedResource(resourceId, {
          tab: "assistant",
          storeCurrentAsPrevious: true,
          queryParams
        });
        setChatAssistantFullscreen(false);
        e.preventDefault();
        return;
      }
      if (target.tagName === "A") {
        const href = target.href;
        if (href.startsWith("fabric://")) {
          let resourceId;
          try {
            const idAndParams = href.split("://")[1];
            const [id] = idAndParams.split("?");
            resourceId = id;
          } catch (e2) {
            console.error(e2);
          }
          if (!resourceId || !isValidUUID(resourceId)) {
            console.error("Invalid resource id", resourceId);
            return;
          }
          showExpandedResource(resourceId, {
            tab: "assistant",
            storeCurrentAsPrevious: true
          });
          setChatAssistantFullscreen(false);
          e.preventDefault();
        }
      }
    };
    div?.addEventListener("click", linkClickHandler);
    return () => {
      div?.removeEventListener("click", linkClickHandler);
    };
  }, [
    showExpandedResource,
    setChatAssistantFullscreen
  ]);
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(RenderedContentWrapper, {
        isResourceList,
        ref: divRef,
        dangerouslySetInnerHTML: {
          __html: markdownContent
        }
      }),
      /* @__PURE__ */ jsx(ChatbotMessageReferencedItems, {
        data: referenceItems
      })
    ]
  });
};
const FabricChatbotAvatar = () => {
  return /* @__PURE__ */ jsx(Avatar, {
    size: 24,
    fixedBgColor: cssVar["color-bg-primary-reverse"],
    children: /* @__PURE__ */ jsx(IconPath16FabricFace, {
      style: {
        color: cssVar["color-text-primary-reverse"]
      }
    })
  });
};
const ChatbotMessage = ({ message }) => {
  const { data: user } = useQueryUserInfo();
  const { diffsActiveMessageId } = useChatbot();
  const isUser = isChatbotUserMessage(message);
  const isAssistant = isChatbotAssistantMessage(message);
  const isContextSwitchInfo = isChatbotContextSwitchInfo(message);
  const allowRepeatingAssistantDiffs = useIsFeatureFlagEnabled(FeatureFlag.ALLOW_REPEATING_ASSISTANT_DIFFS);
  const content = isUser && message.action?.type === ChatbotConversationUserActionType.SUMMARIZE && message.action.subType === ChatbotConversationUserActionSubType.TEXT ? `Summarize highlight:
_“${message.content}”_` : message.content;
  const firstParagraph = message.action ? content : content.substring(0, content.indexOf("\n"));
  const restOfMessage = message.action ? void 0 : content.substring(content.indexOf("\n") + 1);
  const firstRenderedContent = firstParagraph.length > 0 ? firstParagraph : content;
  return /* @__PURE__ */ jsxs(Conversation.Item, {
    as: motion.div,
    ...framerAnimationSlideUp,
    children: [
      isContextSwitchInfo && message.summary && /* @__PURE__ */ jsx(ChatbotMessageSummaryBox, {
        summary: message.summary,
        style: {
          margin: -6,
          marginBottom: ".75rem",
          width: "calc(100% + 12px)"
        }
      }),
      /* @__PURE__ */ jsx(Conversation.ItemHeader, {
        children: isUser ? /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(AvatarUser, {
              user: user ?? {
                name: "",
                id: "anonymous"
              },
              tooltip: void 0,
              size: 24,
              fixedBgColor: !user ? "linear-gradient(225deg, #FF0400 0%, #FFFB00 100%)" : void 0
            }),
            /* @__PURE__ */ jsx(Conversation.ItemHeaderName, {
              children: user?.name || "You"
            })
          ]
        }) : /* @__PURE__ */ jsxs(Fragment, {
          children: [
            /* @__PURE__ */ jsx(FabricChatbotAvatar, {}),
            /* @__PURE__ */ jsx(Conversation.ItemHeaderName, {
              children: "Fabric Assistant"
            })
          ]
        })
      }),
      /* @__PURE__ */ jsxs(ContentBlock, {
        "data-message-id": message.id,
        className: "rounded-lg group",
        role: message.role,
        action: message.action,
        children: [
          /* @__PURE__ */ jsx(RenderedContent, {
            content: firstRenderedContent
          }),
          firstParagraph && restOfMessage || isAssistant && message.resourceIds?.length && !message.action ? /* @__PURE__ */ jsx(Fragment, {
            children: firstParagraph && restOfMessage && /* @__PURE__ */ jsx(RenderedContent, {
              isResourceList: isAssistant && message.resourceIds?.length > 0,
              content: restOfMessage
            })
          }) : null,
          isChatbotAssistantMessage(message) && !message.streaming && (message.hasIncompleteDiffs || message.hasFailedDiffs) && /* @__PURE__ */ jsx(Fragment, {
            children: message.hasFailedDiffs ? /* @__PURE__ */ jsx("p", {
              children: "Failed to generate response, press retry to try again."
            }) : /* @__PURE__ */ jsx("p", {
              children: "Response length limit reached, press continue to keep generating."
            })
          })
        ]
      }),
      !isUser && (message.content.length >= ChatbotMessageActionsMinChars || isChatbotAssistantMessage(message) && (message.hasIncompleteDiffs || message.id === diffsActiveMessageId || allowRepeatingAssistantDiffs)) && /* @__PURE__ */ jsx(ChatbotMessageActions, {
        message,
        style: {
          marginTop: "1rem"
        }
      })
    ]
  });
};
const ContentBlock = styled(Flex).attrs((props) => {
  return {
    direction: "column",
    ...props
  };
}).withConfig({
  displayName: "ChatbotMessage__ContentBlock",
  componentId: "sc-cc263401-2"
})([
  `position:relative;color:`,
  `;font-size:0.8125rem;line-height:1.375rem;gap:4px;`
], cssVar["color-text-primary"]);

export { FabricChatbotAvatar, ChatbotMessage as default };
//# sourceMappingURL=ChatbotMessage.js.map
